import { isAbsoluteUrl } from '@gbds/core/src/ts/utils'
import {
	API_CONNECT_TOKEN_VALIDATION_PATH,
	API_CONNECT_TOKEN_VALIDATION_PATH_EU,
	BRANDS_MY_ACCOUNT_VERIFICATION_URL,
	BRANDS_DETOUR_URL,
	DEFAULT_MY_ACCOUNT_VERIFICATION_URL,
	GM_SERVER_TIME_PATH,
	MYACCOUNT_PROFILE_URL,
} from '../../config/paths'
import { AuthStorageKeys } from '../../config/auth-enumerations'
import { backUpServerTimeUrl, backUpVerifyUrl } from '../../config/verify-header-test'
import { verificationDomains } from '../../config/verification-domains-test'

/**
 * Token Verification Utility File
 */

async function getServerOffset(serverURLArray: string[] = [backUpServerTimeUrl, GM_SERVER_TIME_PATH]): Promise<number> {
	//Make a fetch call
	const serverURL = serverURLArray.pop()
	if (!serverURL) {
		return 0
	}
	const serverResponse = await fetch(serverURL)
	const serverString = await serverResponse.text()
	//Coerce the string into a number, then multiple by 1000 to get the milliseconds
	const serverMilliseconds = Number(serverString) * 1000
	//If the status response is not 200 or a number cannot be parse, try the next URL
	if (serverResponse.status !== 200 || isNaN(serverMilliseconds)) {
		window.console.log('getting server time failed for: ', serverURL)
		return getServerOffset(serverURLArray)
	} else {
		const serverTime = new Date(serverMilliseconds)
		const now = Date.now()
		const serverOffset = now - serverTime.getTime() // (serverTime.getTime = now - serverOffset) should be positive
		sessionStorage.setItem(AuthStorageKeys.serverOffset, String(serverOffset))
		return serverOffset
	}
}

//Make sure time get offset does not return NaN
export async function isTokenExpired(timeToCheck: Date): Promise<boolean> {
	const offset = await getOffset()
	const now = Date.now()
	const difference = now - timeToCheck.getTime() - offset
	return difference > 0
}

//Make sure get offset does not return NaN
export async function getTimeToExpiration(timeToCheck: Date): Promise<number> {
	const offset = await getOffset()
	const now = Date.now()
	return timeToCheck.getTime() - now + offset
}

async function getOffset(): Promise<number> {
	//Try to get the offset from storage first
	//Coercing the string into a number
	const offsetNumber = Number(sessionStorage.getItem(AuthStorageKeys.serverOffset))
	//If offset does not exist or can not be parsed into an int, get the server time
	if (isNaN(offsetNumber)) {
		return await getServerOffset()
	} else {
		return offsetNumber
	}
}

/**
 * Returns a boolean that validates whether the state string is on the same domain as the current page
 * @param state - string representing an absolute or relative url
 */
export function validateStateParam(state: string): boolean {
	if (isAbsoluteUrl(state)) {
		const url = new URL(state)
		return url.origin === window.location.origin
	}
	return true
}

/**
 * Returns the expiration date of a decrypted token
 * @param token - non encrypted token string to parse
 */
export function parseTokenForExpiration(token: string): Date | null {
	try {
		const payload = atob(token.split('.')[1])
		const payloadJSON = JSON.parse(payload)
		const expiration = payloadJSON.exp
		const expirationMilliseconds = Number(expiration)
		return new Date(expirationMilliseconds * 1000)
	} catch (e) {
		console.log('Error parsing token for expiration claim', e)
		return null
	}
}

/**
 * Returns the email claim of the non encrypted token string
 * @param token - non encrypted token string to parse
 */
export function parseTokenForEmail(token: string): string | null {
	try {
		const payload = atob(token.split('.')[1])
		const payloadJSON = JSON.parse(payload)
		return payloadJSON.email
	} catch (e) {
		console.log('Error parsing Token for email claim', e)
		return null
	}
}

/**
 * Returns the name claim of the non encrypted token string
 * @param token - non encrypted token string to parse
 */
export function parseTokenForName(token: string): string | null {
	try {
		const payload = atob(token.split('.')[1])
		const payloadJSON = JSON.parse(payload)
		return payloadJSON.name
	} catch (e) {
		console.log('Error parsing Token for name claim', e)
		return null
	}
}

/**
 * gets the email verification status claim from the given token string
 * @param token - token string to parse
 */
export function getVerificationStatusFromToken(token: string): string | null {
	try {
		const payload = atob(token.split('.')[1])
		const payloadJSON = JSON.parse(payload)
		return payloadJSON.verification_status
	} catch (e) {
		console.log('Error parsing Token for name claim', e)
		return null
	}
}

/**
 * Redirect user to verification page
 * @param redirectPath - Page to redirect to as part of verification flow
 */
export function redirectToVerificationPage(redirectPath?: string): void {
	//For the verification flow, there are two pages that myAccount will redirect to
	//One for the brand site domains, another for every other domain
	let verificationURL
	const country = window?.gbpe?.config?.locale?.split('-')[1]
	const language = window?.gbpe?.config?.locale?.split('-')[0]
	//Store the brand urls in an array from the verification domain enums
	const brandURLs: string[] = []
	for (const value in verificationDomains) {
		brandURLs.push(verificationDomains[value])
	}

	if (redirectPath && redirectPath !== '') {
		verificationURL =
			isAbsoluteUrl(redirectPath) && redirectPath.includes(window.location.origin)
				? new URL(redirectPath)
				: new URL(redirectPath, window.location.origin)
	} else {
		if (brandURLs && brandURLs.includes(window.location.origin)) {
			if (country?.toUpperCase() === 'CA') {
				verificationURL = new URL(`/${language}${BRANDS_MY_ACCOUNT_VERIFICATION_URL}`, window.location.origin)
			} else {
				verificationURL = new URL(BRANDS_MY_ACCOUNT_VERIFICATION_URL, window.location.origin)
			}
		} else {
			verificationURL = new URL(DEFAULT_MY_ACCOUNT_VERIFICATION_URL, window.location.origin)
		}
	}

	if (!window.location.href.toLowerCase().includes(verificationURL.href.toLowerCase())) {
		console.log('here is where we redirect to if email is not verified', verificationURL.href)
		window.location.href = verificationURL.href
	}
}

/**
 * Method used to validate tokens (B2C version 1.0) before determining user is authenticated
 * @param token - decrypted token string to verify
 */
export async function validateToken(token: string): Promise<boolean> {
	const headers = new Headers()
	const bearer = token

	//Append the authorization header with the token
	headers.append('Authorization', bearer)
	headers.append('locale', 'en-US')

	let finalTokenValidationPath = API_CONNECT_TOKEN_VALIDATION_PATH
	const windowURL = window?.location?.origin
	if (windowURL.endsWith('.ca') || windowURL.includes('canada') || windowURL.includes('-ca.')) {
		finalTokenValidationPath = `/en${API_CONNECT_TOKEN_VALIDATION_PATH}`
		headers.set('locale', 'en-CA')
	}
	if (windowURL.includes('europe')) {
		finalTokenValidationPath = `${API_CONNECT_TOKEN_VALIDATION_PATH_EU}`
		headers.set('locale', 'en-CH')
	}
	if (windowURL.includes('cadillacanz') || windowURL.includes('gmspecialtyvehicles')) {
		finalTokenValidationPath = `/au-en${API_CONNECT_TOKEN_VALIDATION_PATH}`
		headers.set('locale', 'en-AU')
	}
	if (windowURL.includes('japan')) {
		finalTokenValidationPath = `${API_CONNECT_TOKEN_VALIDATION_PATH}`
		headers.set('locale', 'ja-JP')
	}

	const options = {
		method: 'GET',
		headers: headers,
	}
	const responseStatus = await fetch(finalTokenValidationPath, options)
		.then(response => response.status)
		.catch(error => {
			console.error('Error has occurred in token validation: ', error)
		})
	if (responseStatus === 404) {
		console.log('API token validation url does not exist in current domain')
		const defaultResponseStatus = await fetch(backUpVerifyUrl, options)
			.then(response => response.status)
			.catch(error => {
				console.error('Error occurred in backup token validation url:', error)
			})
		return defaultResponseStatus !== undefined ? defaultResponseStatus === 200 : false
	} else {
		if (responseStatus !== undefined && responseStatus !== 200) {
			console.log('Token validation failed')
		}
		return responseStatus !== undefined ? responseStatus === 200 : false
	}
}

/**
 * Function to get Region from Profile API
 * @param token
 * @param locale
 * @param region
 * @param profileURL - endpoint to Profile API
 */
export async function getRegionRedirect(
	token: string,
	locale: string,
	region: string,
	profileURL?: string
): Promise<string> {
	const headers = new Headers()

	//Append the authorization header with the token
	headers.append('Authorization', token)
	headers.append('locale', locale)
	headers.append('region', region)

	const options = {
		method: 'GET',
		headers: headers,
	}

	const regionCheckEndpoint = profileURL ? profileURL : MYACCOUNT_PROFILE_URL

	//Handles 200, 404s and CORs errors
	try {
		const regionResponse = await fetch(regionCheckEndpoint, options)
		if (regionResponse.status !== 200) {
			throw new Error('Region Response has failed')
		}
		const regionResponseJSON = await regionResponse.json()
		const country = regionResponseJSON?.country
		const language = locale.split('-')[0]

		//return true if the country exists and it does not match the region the user is currently on
		//e.g. if the country code is US and they are on Canada, return true
		const actionRequired = country !== undefined && country !== region

		//URL we want is /{language-of-current-domain}/detour?region={Account Region}
		const countryGBPE = window?.gbpe?.config?.locale?.split('-')[1]
		console.log(`Logged in User's Country: ${country}\nSite's Detected Country: ${countryGBPE}`)
		let brandsDetourURL = ''

		const detourCountries = [
			'US',
			'KR',
			'CA',
			'CH',
			'SE',
			'DE',
			'FR',
			'GB',
			'NL',
			'NO',
			'LO',
			'IT',
			'AU',
			'NZ',
			'IE',
		]

		if (detourCountries.includes(countryGBPE)) {
			if (countryGBPE === 'US') {
				brandsDetourURL = '/detour'
			} else if (countryGBPE === 'CA') {
				brandsDetourURL = '/en/detour'
			} else {
				brandsDetourURL = `/${countryGBPE}-en/detour`
			}
		} else {
			console.log(`Site's Country Not Configured for Detour\nDetected Site's Country: ${countryGBPE}`)
		}

		return actionRequired === true ? `${brandsDetourURL}` : ''
	} catch (error) {
		console.error('Error getting Region Response', error)
		//Assume it passes so we don't block email verification
		return ''
	}
}

/**
 * Function to parse token and return entitlements
 * @param token
 */
export function parseTokenForEntitlements(token: string): string | null {
	try {
		const payload = atob(token.split('.')[1])
		const payloadJSON = JSON.parse(payload)
		return payloadJSON?.Entitlements
	} catch (e) {
		console.log('Error parsing Token for Entitlements', e)
		return null
	}
}
